import type { ModifierGroup, ModifierOption, Money } from "@workspace/model"

/**
 * PRESENTATION rules for the size group, shared by every item customizer
 * (`ModifierCustomizer` and `FreeOptionCustomizer` — an item routes to one or
 * the other purely on `composite`, and its size group must look identical
 * either way).
 *
 * The size group is identified by a `size:<GUID>` id (backend convention). It
 * gets two tweaks the other groups don't: its options are sorted cheapest-first
 * (ties broken by name), and each option shows the ABSOLUTE price
 * (`basePrice + priceDelta`) instead of the `+delta` other groups show —
 * diners pick a size by its real price, not by a surcharge.
 *
 * Deliberately NOT the PRICING-side size resolution (`sizeGroupOf` /
 * `canonicalSizeId` in @workspace/ordering): what an extra's
 * `priceDeltaBySizeId` is keyed by must be resolved exactly the way the
 * authoritative server-side re-price resolves it. These helpers only decide how
 * the size group LOOKS.
 */
const SIZE_GROUP_ID_PREFIX = "size:"

export function isSizeGroup(group: ModifierGroup): boolean {
  return group.id.startsWith(SIZE_GROUP_ID_PREFIX)
}

export function sortSizeOptions(
  options: readonly ModifierOption[],
  locale: string
): ModifierOption[] {
  return [...options].sort(
    (a, b) =>
      a.priceDelta.amount - b.priceDelta.amount ||
      a.name.localeCompare(b.name, locale)
  )
}

/** The price a size option is actually charged at: `basePrice + priceDelta`. */
export function absoluteSizePrice(
  basePrice: Money,
  option: ModifierOption
): Money {
  return {
    amount: basePrice.amount + option.priceDelta.amount,
    currency: basePrice.currency,
  }
}
